/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file DriverPushBanner.js
 * @description
 * Global foreground banner of the Driver app. A courier is driving, so a tiny
 * toast is useless: this is a large, dark, high-contrast card pinned under the
 * status bar with ONE primary action ("Megnyitás") sized for a thumb, that
 * hides itself after {@link AUTO_HIDE_MS}. It is the fallback handler of
 * `driverPushBus`: a focused route screen that can show the change inline
 * consumes the push first and this banner stays quiet.
 *
 * Mounted once in `App.jsx` (driver mode only) — its presence is also what tells
 * the push bootstrap that the app is in driver mode (no handler → local notification).
 *
 * @module components/driver/DriverPushBanner
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Animated, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Icon from '../management/SafeIcon';
import {useTopInset} from '../../helpers/safeArea';
import {navigateWhenReady} from '../../helpers/navigationRef';
import {DRIVER_EVENT_KEYS, DRIVER_PUSH_ORANGE} from '../../helpers/driverPushPayload';
import {NIGHT} from '../../helpers/managementTheme';
import {registerDriverPushHandler} from '../../helpers/driverPushBus';
import {openDriverTarget} from '../../helpers/driverPushRouting';
import {resolveNotificationNavigationTarget} from '../../helpers/notificationNavigation';
import {TOUCH_TARGET} from './DriverUI';

/** How long the banner stays before hiding itself. */
export const AUTO_HIDE_MS = 8000;

const EVENT_ICONS = {
    [DRIVER_EVENT_KEYS.routeAssigned]: 'map-marker-plus',
    [DRIVER_EVENT_KEYS.routeUpdated]: 'alert-decagram',
    [DRIVER_EVENT_KEYS.routeUnassigned]: 'close-octagon',
    [DRIVER_EVENT_KEYS.routeCancelled]: 'close-octagon',
    [DRIVER_EVENT_KEYS.stockLoaded]: 'package-variant-closed',
    [DRIVER_EVENT_KEYS.changeRequestResolved]: 'clipboard-check-outline',
};

/**
 * @returns {JSX.Element|null} The banner host (renders nothing while idle).
 */
const DriverPushBanner = () => {
    const {t} = useTranslation();
    const topInset = useTopInset();
    const [item, setItem] = useState(null);
    const progress = useRef(new Animated.Value(0)).current;
    const timer = useRef(null);

    const clearTimer = useCallback(() => {
        if (timer.current) {
            globalThis.clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    const hide = useCallback(() => {
        clearTimer();
        Animated.timing(progress, {toValue: 0, duration: 180, useNativeDriver: true}).start(({finished}) => {
            if (finished) {
                setItem(null);
            }
        });
    }, [clearTimer, progress]);

    useEffect(() => registerDriverPushHandler((payload) => {
        clearTimer();
        progress.stopAnimation();
        setItem(payload);
        Animated.spring(progress, {toValue: 1, useNativeDriver: true, friction: 8, tension: 90}).start();
        timer.current = globalThis.setTimeout(hide, AUTO_HIDE_MS);

        return true;
    }), [clearTimer, hide, progress]);

    useEffect(() => clearTimer, [clearTimer]);

    const open = useCallback(() => {
        if (!item) {
            return;
        }
        const data = {
            type: 'driver',
            eventKey: item.eventKey,
            routePlanId: item.routePlanId,
            stopId: item.stopId,
            warehouseId: item.warehouseId,
            changeRequestId: item.changeRequestId,
            status: item.status,
            changeSummary: item.changeSummary,
        };
        const message = {data};
        const target = resolveNotificationNavigationTarget(message);

        hide();
        if (target?.name) {
            openDriverTarget(target, message, navigateWhenReady).catch(() => {});
        }
    }, [hide, item]);

    if (!item) {
        return null;
    }

    const translateY = progress.interpolate({inputRange: [0, 1], outputRange: [-220, 0]});

    return (
        <Animated.View
            style={[styles.wrap, {top: topInset + 8, opacity: progress, transform: [{translateY}]}]}
            accessibilityRole="alert"
            accessibilityLiveRegion="assertive"
        >
            <View style={styles.row}>
                <View style={styles.iconTile}>
                    <Icon name={EVENT_ICONS[item.eventKey] || 'truck-delivery-outline'} size={30} color="#FFFFFF" />
                </View>
                <View style={styles.main}>
                    <Text style={styles.title} numberOfLines={2}>
                        {item.title || t('driver_push_default_title', {defaultValue: 'Sofőr értesítés'})}
                    </Text>
                    {!!item.body && <Text style={styles.body} numberOfLines={3}>{item.body}</Text>}
                </View>
                <TouchableOpacity
                    style={styles.close}
                    onPress={hide}
                    accessibilityLabel={t('driver_push_close', {defaultValue: 'Bezárás'})}
                >
                    <Icon name="close" size={24} color="#CBD5E1" />
                </TouchableOpacity>
            </View>
            <TouchableOpacity style={styles.primary} onPress={open} activeOpacity={0.85}>
                <Text style={styles.primaryText}>{t('driver_push_open', {defaultValue: 'Megnyitás'})}</Text>
                <Icon name="arrow-right" size={26} color="#FFFFFF" />
            </TouchableOpacity>
        </Animated.View>
    );
};

const styles = StyleSheet.create({
    wrap: {
        position: 'absolute',
        left: 10,
        right: 10,
        zIndex: 9999,
        elevation: 24,
        backgroundColor: NIGHT.bg,
        borderRadius: 28,
        borderLeftWidth: 8,
        borderLeftColor: DRIVER_PUSH_ORANGE,
        padding: 14,
        gap: 12,
        shadowColor: '#000000',
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.35,
        shadowRadius: 16,
    },
    row: {flexDirection: 'row', alignItems: 'flex-start', gap: 12},
    iconTile: {width: 52, height: 52, borderRadius: 26, backgroundColor: DRIVER_PUSH_ORANGE, alignItems: 'center', justifyContent: 'center'},
    main: {flex: 1},
    title: {color: '#FFFFFF', fontSize: 20, fontWeight: '800', letterSpacing: -0.3},
    body: {color: '#E2E8F0', fontSize: 16, fontWeight: '600', marginTop: 4},
    close: {width: 52, height: 52, alignItems: 'center', justifyContent: 'center', marginTop: -8, marginRight: -8},
    primary: {
        minHeight: TOUCH_TARGET + 8,
        borderRadius: 30,
        backgroundColor: DRIVER_PUSH_ORANGE,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
    },
    primaryText: {color: '#FFFFFF', fontSize: 20, fontWeight: '800'},
});

export default DriverPushBanner;
